import React, { useState } from 'react';
import { Select } from '../ui/Select';
import { MultiSelect } from '../ui/MultiSelect';
import { Info } from 'lucide-react';
export function ClassificationTab() {
  const [loanCategory, setLoanCategory] = useState('');
  const [loanTypes, setLoanTypes] = useState<string[]>([]);
  const [loanRiskProfiles, setLoanRiskProfiles] = useState<string[]>([]);
  const [merchantTypes, setMerchantTypes] = useState<string[]>([]);
  const [partnerType, setPartnerType] = useState('');
  const [repaymentStructures, setRepaymentStructures] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const isB2B = loanCategory === 'b2b';
  const showPartnerType = merchantTypes.includes('partner') || merchantTypes.includes('both');
  const loanCategoryOptions = [{
    value: '',
    label: 'Select loan category...'
  }, {
    value: 'b2b',
    label: 'B2B Financing (Business Lending)'
  }, {
    value: 'consumer',
    label: 'Consumer Lending (Individual Customer)'
  }];
  const loanTypeOptionsB2B = [{
    value: 'stwc',
    label: 'STWC (Short-Term Working Capital Loan)'
  }, {
    value: 'mtbl',
    label: 'MTBL (Medium-Term Business Loan)'
  }, {
    value: 'aos',
    label: 'AOS (Advance-on-Settlement)'
  }, {
    value: 'seasonal',
    label: 'Seasonal Loan'
  }, {
    value: 'rcl',
    label: 'RCL (Revolving Credit Line)'
  }, {
    value: 'pts',
    label: 'PTS (Payment to Supplier)'
  }, {
    value: 'ecf',
    label: 'ECF (Emergency Cash Facility)'
  }];
  const loanTypeOptionsConsumer = [{
    value: 'personal',
    label: 'Personal Loan'
  }];
  const loanRiskProfileOptions = [{
    value: 'collateralized',
    label: 'Fully Collateralized'
  }, {
    value: 'unsecured',
    label: 'Unsecured'
  }, {
    value: 'semi',
    label: 'Semi-Secured'
  }];
  const merchantTypeOptions = [{
    value: 'new',
    label: 'New Merchant'
  }, {
    value: 'partner',
    label: 'Partner Merchant'
  }, {
    value: 'both',
    label: 'Both (New and Partner Merchants)'
  }];
  const partnerTypeOptions = [{
    value: '',
    label: 'Select partner type...'
  }, {
    value: 'tapsys',
    label: 'Tapsys'
  }, {
    value: 'abc',
    label: 'ABC'
  }];
  const repaymentStructureOptions = [{
    value: 'equal',
    label: 'Equal Installment Options (Fixed monthly or weekly installments)'
  }, {
    value: 'custom',
    label: 'Custom Installment Option (Flexible repayments based on cash flow)'
  }, {
    value: 'bullet',
    label: 'Bullet (Lump-sum repayment at the end of the term)'
  }, {
    value: 'pos',
    label: 'POS-based Repayment (Auto-deducted from POS sales)'
  }];
  const tooltips = {
    loanCategory: 'Categorizes the loan product into Business or Consumer Lending for eligibility, underwriting, and product grouping.',
    loanType: 'Defines the specific nature of the loan (e.g., short-term working capital or personal loan). Impacts repayment schedule and interest calculation.',
    loanRiskProfile: 'Categorizes the loan based on collateralization (fully collateralized, unsecured, or semi-secured) for risk management.',
    merchantType: 'Defines eligible customer segments for loan access (e.g., new merchants or those with an existing partnership).',
    partnerType: 'Defines the external partner ecosystem or platform through which the loan is distributed.',
    repaymentStructure: 'Defines how the loan will be repaid (e.g., equal installments, lump-sum payment, or POS sales deductions).'
  };
  const renderFieldWithTooltip = (label: string, tooltipKey: keyof typeof tooltips, children: React.ReactNode) => {
    return <div data-id="element-516">
        <div className="flex items-center gap-1 mb-1.5" data-id="element-517">
          <label className="block text-sm font-medium text-gray-700" data-id="element-518">
            {label}
            <span className="text-red-500 ml-1" data-id="element-519">*</span>
          </label>
          <div className="relative" data-id="element-520">
            <button onMouseEnter={() => setShowTooltip(tooltipKey)} onMouseLeave={() => setShowTooltip(null)} className="text-gray-400 hover:text-blue-600 transition-colors" type="button" data-id="element-521">
              <Info className="h-4 w-4" data-id="element-522" />
            </button>
            {showTooltip === tooltipKey && <div className="absolute left-0 top-6 z-50 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl" data-id="element-523">
                <p className="leading-relaxed" data-id="element-524">{tooltips[tooltipKey]}</p>
                <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45" data-id="element-525"></div>
              </div>}
          </div>
        </div>
        {children}
      </div>;
  };
  return <div className="space-y-6 max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-526">
      <div data-id="element-527">
        <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-528">
          Product Classification
        </h3>
        <p className="text-sm text-gray-500" data-id="element-529">
          Define the loan product category, type, and risk profile
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm space-y-6" data-id="element-530">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-531">
          {/* Loan Category */}
          {renderFieldWithTooltip('Loan Category', 'loanCategory', <Select value={loanCategory} onChange={e => {
          setLoanCategory(e.target.value);
          setLoanTypes([]); // Reset loan types when category changes
          setMerchantTypes([]); // Reset merchant types
          setPartnerType(''); // Reset partner type
        }} options={loanCategoryOptions} data-id="element-532" />)}

          {/* Loan Type - Multi-select */}
          {renderFieldWithTooltip('Loan Type', 'loanType', <MultiSelect value={loanTypes} onChange={setLoanTypes} disabled={!loanCategory} options={isB2B ? loanTypeOptionsB2B : loanTypeOptionsConsumer} placeholder="Select loan types..." data-id="element-533" />)}

          {/* Loan Risk Profile - Multi-select */}
          {renderFieldWithTooltip('Loan Risk Profile', 'loanRiskProfile', <MultiSelect value={loanRiskProfiles} onChange={setLoanRiskProfiles} options={loanRiskProfileOptions} placeholder="Select risk profiles..." data-id="element-534" />)}

          {/* Merchant Type - Only for B2B */}
          {isB2B && renderFieldWithTooltip('Merchant Type', 'merchantType', <MultiSelect value={merchantTypes} onChange={values => {
          setMerchantTypes(values);
          // Reset partner type if partner is deselected
          if (!values.includes('partner') && !values.includes('both')) {
            setPartnerType('');
          }
        }} options={merchantTypeOptions} placeholder="Select merchant types..." data-id="element-535" />)}

          {/* Partner Type - Only if Partner Merchant is selected */}
          {isB2B && showPartnerType && renderFieldWithTooltip('Partner Type', 'partnerType', <Select value={partnerType} onChange={e => setPartnerType(e.target.value)} options={partnerTypeOptions} data-id="element-536" />)}

          {/* Repayment Structure - Multi-select */}
          {renderFieldWithTooltip('Repayment Structure', 'repaymentStructure', <MultiSelect value={repaymentStructures} onChange={setRepaymentStructures} options={repaymentStructureOptions} placeholder="Select repayment structures..." data-id="element-537" />)}
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-538">
        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-539">
          Classification Guidelines
        </h4>
        <div className="space-y-2 text-sm text-gray-600" data-id="element-540">
          <div className="flex items-start" data-id="element-541">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0" data-id="element-542"></span>
            <span data-id="element-543">
              <strong data-id="element-544">Loan Category:</strong> Determines the primary customer
              segment (Business or Consumer)
            </span>
          </div>
          <div className="flex items-start" data-id="element-545">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0" data-id="element-546"></span>
            <span data-id="element-547">
              <strong data-id="element-548">Loan Type:</strong> Multiple loan types can be selected
              for flexible product offerings
            </span>
          </div>
          <div className="flex items-start" data-id="element-549">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0" data-id="element-550"></span>
            <span data-id="element-551">
              <strong data-id="element-552">Merchant Type:</strong> Only applicable for B2B Financing
              products
            </span>
          </div>
          <div className="flex items-start" data-id="element-553">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0" data-id="element-554"></span>
            <span data-id="element-555">
              <strong data-id="element-556">Partner Type:</strong> Required when Partner Merchant is
              selected
            </span>
          </div>
        </div>
      </div>
    </div>;
}